import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  AcademicCapIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
  ClipboardDocumentListIcon,
  UserIcon,
  ShoppingBagIcon,
  TicketIcon,
} from '@heroicons/react/24/solid';
import { useUser } from '../contexts/UserContext';

interface SidebarProps {
  onClose?: () => void;
}

interface MenuItem {
  name: string;
  href: string;
  icon: any;
  roles: string[];
}

interface MenuCategory {
  label: string;
  id: string;
  icon: any;
  items: MenuItem[];
  defaultExpanded?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isModerator, isTeacher } = useUser();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['category-home']));

  const menuCategories: MenuCategory[] = [
    {
      label: 'الرئيسية',
      id: 'category-home',
      icon: HomeIcon,
      defaultExpanded: true,
      items: [
        { name: 'لوحة التحكم', href: '/', icon: HomeIcon, roles: ['Moderator', 'Teacher'] },
      ],
    },
    {
      label: 'المستخدمون',
      id: 'users',
      icon: UserGroupIcon,
      items: [
        { name: 'مستخدمي النظام', href: '/system-users', icon: UserGroupIcon, roles: ['Moderator'] },
        { name: 'الطلاب', href: '/students', icon: AcademicCapIcon, roles: ['Moderator', 'Teacher'] },
        { name: 'المعلمون', href: '/teachers', icon: UserIcon, roles: ['Moderator'] },
      ],
    },
    {
      label: 'الدورات والبرامج',
      id: 'courses',
      icon: BookOpenIcon,
      items: [
        { name: 'المواد الدراسية', href: '/subjects', icon: ClipboardDocumentListIcon, roles: ['Moderator'] },
        { name: 'الباقات', href: '/courses', icon: BookOpenIcon, roles: ['Moderator', 'Teacher'] },
        { name: 'الكتب', href: '/books', icon: BookOpenIcon, roles: ['Moderator'] },
      ],
    },
    {
      label: 'الأكواد',
      id: 'codes',
      icon: ({ className }: { className?: string }) => (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
        </svg>
      ),
      items: [
        { 
          name: 'أكواد المحاضرات', 
          href: '/unit-codes', 
          icon: ({ className }: { className?: string }) => (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
            </svg>
          ),
          roles: ['Moderator', 'Teacher'] 
        },
        { 
          name: 'أكواد الباقات', 
          href: '/codes', 
          icon: ({ className }: { className?: string }) => (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          ),
          roles: ['Moderator', 'Teacher'] 
        },
      ],
    },
    {
      label: 'المبيعات',
      id: 'sales',
      icon: ShoppingBagIcon,
      items: [
        { name: 'الطلبات', href: '/orders', icon: ShoppingBagIcon, roles: ['Moderator'] },
        { name: 'أكواد الخصم', href: '/promo-codes', icon: TicketIcon, roles: ['Moderator'] },
      ],
    },
    {
      label: 'التواصل',
      id: 'communication',
      icon: ChatBubbleLeftRightIcon,
      items: [
        { name: 'اتصل بنا', href: '/contact-us', icon: ChatBubbleLeftRightIcon, roles: ['Moderator'] },
      ],
    },
  ];

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleLogout = async () => {
    try {
      console.log('Starting logout process...');
      await logout();
      console.log('Logout completed, navigating to login...');
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      await logout();
      navigate('/login');
    }
  };

  const renderIcon = (icon: any, className: string) => {
    if (typeof icon === 'function') {
      return icon({ className });
    }
    const IconComponent = icon;
    return <IconComponent className={className} />;
  };

  // Filter categories and items based on user role
  const filteredCategories = menuCategories.map(category => ({
    ...category,
    items: category.items.filter(item => {
      if (!user) return false;
      return item.roles.includes(user.role);
    })
  })).filter(category => category.items.length > 0);

  return (
    <div className="w-64 h-full bg-white shadow-lg" dir="rtl">
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-800">لوحة الإدارة</h1>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        
        {/* User Info Section */}
        {user && (
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="text-gray-800">
              <p className="text-sm font-medium">{user.name}</p>
              <p className="text-xs text-gray-500">
                {user.role === 'Teacher' ? 'معلم' : 'مشرف'}
              </p>
            </div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto admin-side-menu--hierarchy">
          {filteredCategories.map((category) => {
            const isExpanded = expandedCategories.has(category.id);
            return (
              <div
                key={category.id}
                className={`admin-side-menu--category ${isExpanded ? 'expanded' : ''}`}
                id={category.id}
              >
                <div
                  className="admin-side-menu--top-level-item gap-8 cursor-pointer"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="flex items-center gap-8">
                    {renderIcon(category.icon, 'admin-side-menu-icon w-5 h-5')}
                    <span className="lbl-small grey2 grey1-dark weight-500 text-sm font-medium text-gray-700">
                      {category.label}
                    </span>
                  </div>
                  {isExpanded ? (
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
                
                {isExpanded && (
                  <div className="admin-side-menu--category--items pr-15">
                    {category.items.map((item) => {
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => onClose && onClose()}
                          className={`admin-side-menu--item text-dec-none flex items-center justify-between gap-4 cursor-pointer ${
                            isActive ? 'active' : ''
                          }`}
                        >
                          <span className="flex items-center gap-4">
                            {renderIcon(item.icon, 'w-4 h-4 text-gray-500')}
                            <span className={`lbl-small grey3 grey2-dark weight-500 text-sm ${
                              isActive ? 'text-blue-600 font-medium' : 'text-gray-600'
                            }`}>
                              {item.name}
                            </span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout button */}
        <div className="px-2 py-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full text-white hover:bg-red-600 bg-red-500 group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200"
          >
            <ArrowRightOnRectangleIcon
              className="text-white mr-3 flex-shrink-0 h-6 w-6 transition-colors duration-200"
              aria-hidden="true"
            />
            تسجيل الخروج
          </button>
        </div>
      </div>

      <style>{`
        .admin-side-menu--hierarchy {
          padding: 0.5rem 0;
        }

        .admin-side-menu--category {
          margin-bottom: 0.25rem;
        }

        .admin-side-menu--top-level-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          transition: background-color 0.2s;
        }

        .admin-side-menu--top-level-item:hover {
          background-color: #f3f4f6;
        }

        .admin-side-menu--category--items {
          padding-right: 3.75rem;
          padding-top: 0.25rem;
          padding-bottom: 0.25rem;
        }

        .admin-side-menu--item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 1rem;
          margin: 0.125rem 0;
          border-radius: 0.375rem;
          transition: background-color 0.2s, color 0.2s;
          text-decoration: none;
        }

        .admin-side-menu--item:hover {
          background-color: #f3f4f6;
        }

        .admin-side-menu--item.active {
          background-color: #eff6ff;
          color: #2563eb;
        }

        .admin-side-menu--item.active .lbl-small {
          color: #2563eb !important;
          font-weight: 500;
        }

        .admin-side-menu--item.active svg {
          color: #2563eb;
        }

        .admin-side-menu-icon {
          flex-shrink: 0;
        }

        .gap-8 {
          gap: 2rem;
        }

        .gap-4 {
          gap: 1rem;
        }

        .pr-15 {
          padding-right: 3.75rem;
        }

        .text-dec-none {
          text-decoration: none;
        }

        .cursor-pointer {
          cursor: pointer;
        }

        .lbl-small {
          font-size: 0.875rem;
          line-height: 1.25rem;
        }

        .weight-500 {
          font-weight: 500;
        }

        .grey2 {
          color: #6b7280;
        }

        .grey1-dark {
          color: #374151;
        }

        .grey3 {
          color: #9ca3af;
        }

        .grey2-dark {
          color: #4b5563;
        }
      `}</style>
    </div>
  );
};

export default Sidebar;
